// User-facing support messaging: one private conversation with the Steadily
// support team. Resolve markers segment the stream into issues; any new
// message re-opens the conversation. Route: /support (behind ProtectedRoute).

import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, LifeBuoy, Send } from 'lucide-react';
import { getApiUrl } from '../utils/apiHelpers';
import { useSupport } from '../hooks/useSupport';
import { usePageVisible } from '../hooks/usePageVisible';

const BODY_MAX = 5000;
const POLL_INTERVAL_MS = 10000;

export function formatMessageDate(value) {
  if (!value) return '';
  // SQLite returns 'YYYY-MM-DD HH:MM:SS' (UTC, no zone); Postgres serializes
  // to ISO. Normalize the former so it isn't parsed as local time.
  const iso = typeof value === 'string' && !value.includes('T') ? value.replace(' ', 'T') + 'Z' : value;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// viewerIsAgent flips the perspective for the agent inbox: agent messages
// become "You" on the right, user messages take userLabel on the left.
export function MessageStream({ messages, viewerIsAgent = false, userLabel = 'You' }) {
  return (
    <div className="space-y-4">
      {messages.map((message) => {
        if (message.kind === 'resolved') {
          return (
            <div key={message.id} className="flex items-center gap-3 py-1">
              <div className="flex-1 border-t border-[var(--color-divider)]" />
              <span className="inline-flex items-center gap-1.5 text-xs text-[var(--color-ink-light)] font-serif italic">
                <CheckCircle2 className="w-3.5 h-3.5" />
                Issue resolved · {formatMessageDate(message.created_at)}
              </span>
              <div className="flex-1 border-t border-[var(--color-divider)]" />
            </div>
          );
        }
        const isAgent = message.is_agent;
        const isViewer = viewerIsAgent ? isAgent : !isAgent;
        return (
          <div key={message.id} className={`flex ${isViewer ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] rounded-2xl px-4 py-3 border ${
              isViewer
                ? 'bg-[var(--color-surface-card)] border-[var(--color-divider)]'
                : 'bg-[var(--color-paper-warm)] border-[var(--color-divider)]'
            }`}>
              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-xs font-semibold text-[var(--color-ink-light)]">
                  {isAgent ? (viewerIsAgent ? 'You' : 'Steadily Support') : (viewerIsAgent ? userLabel : 'You')}
                </span>
                <span className="text-[11px] text-[var(--color-ink-subtle)]">
                  {formatMessageDate(message.created_at)}
                </span>
              </div>
              <p className="whitespace-pre-wrap break-words text-[var(--color-ink-main)] leading-relaxed text-[15px]">
                {message.body}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SupportPage() {
  const apiUrl = getApiUrl();
  const { conversation, messages, isLoading, error, fetchConversation, refreshConversation, sendMessage, resolve } = useSupport(apiUrl);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [actionError, setActionError] = useState('');
  const bottomRef = useRef(null);
  const visible = usePageVisible();
  const nearBottomRef = useRef(true);
  const justSentRef = useRef(false);

  useEffect(() => {
    fetchConversation();
  }, [fetchConversation]);

  // Keep the thread current while it's actually on screen. The leading call
  // catches up immediately on tab return / WebView foreground; hiding the tab
  // clears the interval so backgrounded apps stay quiet.
  useEffect(() => {
    if (!visible) return;
    refreshConversation();
    const id = setInterval(refreshConversation, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [visible, refreshConversation]);

  // The page scrolls the document, so track whether the user is near the
  // bottom; a background poll must never yank someone reading history.
  useEffect(() => {
    const onScroll = () => {
      nearBottomRef.current =
        window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 150;
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (nearBottomRef.current || justSentRef.current) {
      justSentRef.current = false;
      bottomRef.current?.scrollIntoView({ block: 'end' });
    }
  }, [messages.length]);

  const handleSend = async (e) => {
    e.preventDefault();
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    setActionError('');
    try {
      justSentRef.current = true;
      await sendMessage(body);
      setDraft('');
    } catch (err) {
      setActionError(err.message);
    } finally {
      setSending(false);
    }
  };

  const handleResolve = async () => {
    if (sending) return;
    setSending(true);
    setActionError('');
    try {
      await resolve();
    } catch (err) {
      setActionError(err.message);
    } finally {
      setSending(false);
    }
  };

  const isOpen = conversation?.status === 'open';

  return (
    <div className="min-h-screen bg-[var(--color-paper-bg)] text-[var(--color-ink-main)]">
      <nav className="px-4 md:px-8 pt-6 pb-4 max-w-3xl mx-auto flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group">
          <div className="w-10 h-10 flex items-center justify-center bg-[var(--color-surface-card)] rounded-xl shadow-sm border border-[var(--color-divider)] p-1 transition-transform group-hover:scale-105">
            <img src="/android-chrome-192x192.png" alt="Steadily.me Logo" className="w-full h-full object-contain rounded-lg" />
          </div>
          <span className="font-serif font-bold text-xl text-[var(--color-ink-main)]">Steadily.me</span>
        </Link>
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-[var(--color-ink-light)] hover:text-[var(--color-ink-main)] transition-colors font-serif">
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </Link>
      </nav>

      <main className="px-4 md:px-8 pb-24 max-w-3xl mx-auto">
        <header className="pt-6 pb-8 border-b border-[var(--color-divider)] flex items-end justify-between gap-4">
          <div>
            <h1 className="text-4xl font-bold font-serif tracking-tight mb-2">Support</h1>
            <p className="text-sm text-[var(--color-ink-light)]">
              A private conversation with the Steadily team. We reply by message and email you when we do.
            </p>
          </div>
          {isOpen && messages.length > 0 && (
            <button
              onClick={handleResolve}
              disabled={sending}
              className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-xl border border-[var(--color-divider)] bg-[var(--color-surface-card)] text-[var(--color-ink-light)] hover:text-[var(--color-ink-main)] transition-colors flex-shrink-0 disabled:opacity-50"
            >
              <CheckCircle2 className="w-4 h-4" />
              Mark resolved
            </button>
          )}
        </header>

        <div className="mt-8">
          {isLoading ? (
            <p className="text-[var(--color-ink-light)] text-sm py-12 text-center">Loading your conversation…</p>
          ) : error ? (
            <p className="text-red-600 text-sm py-12 text-center">{error}</p>
          ) : messages.length === 0 ? (
            <div className="py-12 text-center">
              <LifeBuoy className="w-10 h-10 mx-auto mb-4 text-[var(--color-ink-subtle)]" />
              <h2 className="font-serif font-bold text-xl mb-2">How can we help?</h2>
              <p className="text-sm text-[var(--color-ink-light)] max-w-md mx-auto">
                Send us a message below — questions, bug reports, or anything else. We'll reply here and
                let you know by email.
              </p>
            </div>
          ) : (
            <MessageStream messages={messages} />
          )}
          <div ref={bottomRef} />
        </div>

        {!isLoading && !error && (
          <form onSubmit={handleSend} className="mt-8 sticky bottom-0 bg-[var(--color-paper-bg)] pb-4 pt-2">
            {conversation?.status === 'resolved' && (
              <p className="text-xs text-[var(--color-ink-light)] mb-2 font-serif italic">
                This conversation is resolved — sending a new message will reopen it.
              </p>
            )}
            {actionError && <p className="text-red-600 text-sm mb-2">{actionError}</p>}
            <div className="flex items-end gap-2">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value.slice(0, BODY_MAX))}
                placeholder="Write a message…"
                rows={3}
                className="flex-1 resize-none rounded-2xl border border-[var(--color-divider)] bg-[var(--color-surface-input)] px-4 py-3 text-[15px] text-[var(--color-ink-main)] placeholder-[var(--color-ink-subtle)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-warm)]"
              />
              <button
                type="submit"
                disabled={!draft.trim() || sending}
                className="inline-flex items-center gap-1.5 px-4 py-3 rounded-2xl bg-[var(--color-ink-main)] text-[var(--color-paper-bg)] text-sm font-semibold disabled:opacity-40 transition-opacity"
              >
                <Send className="w-4 h-4" />
                Send
              </button>
            </div>
          </form>
        )}
      </main>
    </div>
  );
}

export default SupportPage;
